// Comprehensive security utilities for data protection and validation
import { logger } from '@/utils/logger';

// Input sanitization utilities
export class InputSanitizer {
  // HTML sanitization to prevent XSS
  static sanitizeHtml(input: string): string {
    if (!input) return '';
    
    return input
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;')
      .replace(/&/g, '&amp;'); // This should be last
  }

  // SQL injection prevention for user inputs
  static sanitizeSql(input: string): string {
    if (!input) return '';
    
    // Remove common SQL injection patterns
    const dangerous = [
      /('|\\'|;|\||\\*|%|<|>|{|})/gi,
      /(union|select|insert|update|delete|drop|create|alter|exec|execute)/gi,
      /(script|javascript|vbscript|onload|onerror|onclick)/gi
    ];
    
    let sanitized = input;
    dangerous.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });
    
    return sanitized.trim();
  }

  // Email validation with comprehensive checks
  static validateEmail(email: string): { isValid: boolean; reason?: string } {
    if (!email) return { isValid: false, reason: 'Email is required' };
    
    // Basic format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, reason: 'Invalid email format' };
    }
    
    // Length checks
    if (email.length > 254) {
      return { isValid: false, reason: 'Email too long' };
    }
    
    // Domain checks
    const [local, domain] = email.split('@');
    if (local.length > 64) {
      return { isValid: false, reason: 'Local part too long' };
    }
    
    // Suspicious patterns
    const suspiciousPatterns = [
      /\.\./,  // consecutive dots
      /^\./, // starts with dot
      /\.$/, // ends with dot
      /\+{2,}/, // multiple plus signs
    ];
    
    if (suspiciousPatterns.some(pattern => pattern.test(email))) {
      return { isValid: false, reason: 'Invalid email pattern' };
    }
    
    return { isValid: true };
  }

  // Password strength validation
  static validatePassword(password: string): { 
    isValid: boolean; 
    score: number; 
    suggestions: string[] 
  } {
    if (!password) {
      return { 
        isValid: false, 
        score: 0, 
        suggestions: ['Password is required'] 
      };
    }

    const suggestions: string[] = [];
    let score = 0;

    // Length check
    if (password.length < 8) {
      suggestions.push('Use at least 8 characters');
    } else if (password.length >= 12) {
      score += 2;
    } else {
      score += 1;
    }

    // Character variety checks
    if (!/[a-z]/.test(password)) {
      suggestions.push('Include lowercase letters');
    } else {
      score += 1;
    }

    if (!/[A-Z]/.test(password)) {
      suggestions.push('Include uppercase letters');
    } else {
      score += 1;
    }

    if (!/\d/.test(password)) {
      suggestions.push('Include numbers');
    } else {
      score += 1;
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      suggestions.push('Include special characters');
    } else {
      score += 1;
    }

    // Common patterns check
    const commonPatterns = [
      /123456/,
      /password/i,
      /qwerty/i,
      /abc123/i,
      /(.)\1{2,}/, // repeated characters
    ];

    if (commonPatterns.some(pattern => pattern.test(password))) {
      suggestions.push('Avoid common patterns and repeated characters');
      score = Math.max(0, score - 2);
    }

    const isValid = score >= 4 && suggestions.length === 0;
    
    return { isValid, score, suggestions };
  }

  // Username validation
  static validateUsername(username: string): { isValid: boolean; reason?: string } {
    if (!username) return { isValid: false, reason: 'Username is required' };
    
    if (username.length < 3) {
      return { isValid: false, reason: 'Username must be at least 3 characters' };
    }
    
    if (username.length > 30) {
      return { isValid: false, reason: 'Username must be less than 30 characters' };
    }
    
    // Only allow alphanumeric, underscore, and hyphen
    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      return { isValid: false, reason: 'Username can only contain letters, numbers, underscore, and hyphen' };
    }
    
    // Reserved usernames
    const reserved = [
      'admin', 'root', 'moderator', 'support', 'help', 'api', 'www',
      'mail', 'ftp', 'test', 'guest', 'anonymous', 'null', 'undefined'
    ];
    
    if (reserved.includes(username.toLowerCase())) {
      return { isValid: false, reason: 'Username is reserved' };
    }
    
    return { isValid: true };
  }
}

// Rate limiting utility
export class RateLimiter {
  private static attempts = new Map<string, { count: number; firstAttempt: number; blocked: boolean }>();
  
  static checkLimit(identifier: string, maxAttempts: number = 5, windowMs: number = 15 * 60 * 1000): {
    allowed: boolean;
    remaining: number;
    resetTime: number;
  } {
    const now = Date.now();
    const key = `rate_limit_${identifier}`;
    const existing = this.attempts.get(key);
    
    // Clean up old entries
    if (existing && (now - existing.firstAttempt) > windowMs) {
      this.attempts.delete(key);
    }
    
    const current = this.attempts.get(key) || { count: 0, firstAttempt: now, blocked: false };
    
    if (current.blocked && (now - current.firstAttempt) < windowMs) {
      return {
        allowed: false,
        remaining: 0,
        resetTime: current.firstAttempt + windowMs
      };
    }
    
    if (current.count >= maxAttempts) {
      current.blocked = true;
      this.attempts.set(key, current);
      
      logger.warn('Rate limit exceeded', {
        component: 'RateLimiter',
        action: 'checkLimit'
      }, {
        identifier,
        attempts: current.count,
        windowMs
      });
      
      return {
        allowed: false,
        remaining: 0,
        resetTime: current.firstAttempt + windowMs
      };
    }
    
    return {
      allowed: true,
      remaining: maxAttempts - current.count,
      resetTime: current.firstAttempt + windowMs
    };
  }
  
  static recordAttempt(identifier: string): void {
    const now = Date.now();
    const key = `rate_limit_${identifier}`;
    const existing = this.attempts.get(key);
    
    if (existing) {
      existing.count++;
      this.attempts.set(key, existing);
    } else {
      this.attempts.set(key, { count: 1, firstAttempt: now, blocked: false });
    }
  }
  
  static reset(identifier: string): void {
    const key = `rate_limit_${identifier}`;
    this.attempts.delete(key);
  }
}

// Content Security Policy utilities
export class CSPManager {
  static generateNonce(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }
  
  static getCSPHeader(nonce?: string): string {
    const policies = [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://fonts.gstatic.com",
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://fonts.gstatic.com",
      "img-src 'self' data: https: blob:",
      "font-src 'self' https://fonts.gstatic.com data:",
      "connect-src 'self' https: wss:",
      "media-src 'self' data: blob:",
      "object-src 'none'",
      "frame-ancestors 'none'",
      "base-uri 'self'",
      "form-action 'self'"
    ];
    
    if (nonce) {
      policies[1] = `script-src 'self' 'nonce-${nonce}' https://fonts.googleapis.com https://fonts.gstatic.com`;
      policies[2] = `style-src 'self' 'nonce-${nonce}' https://fonts.googleapis.com https://fonts.gstatic.com`;
    }
    
    return policies.join('; ');
  }
}

// Data encryption utilities (for client-side encryption of sensitive data)
export class DataProtection {
  // Simple encryption for non-critical data (use proper encryption for production)
  static encrypt(text: string, key: string): string {
    try {
      // This is a simple XOR encryption - replace with proper encryption in production
      let result = '';
      for (let i = 0; i < text.length; i++) {
        result += String.fromCharCode(
          text.charCodeAt(i) ^ key.charCodeAt(i % key.length)
        );
      }
      return btoa(result);
    } catch (error) {
      logger.error('Encryption failed', {
        component: 'DataProtection',
        action: 'encrypt'
      }, error);
      return text; // Fallback to plaintext
    }
  }
  
  static decrypt(encryptedText: string, key: string): string {
    try {
      const text = atob(encryptedText);
      let result = '';
      for (let i = 0; i < text.length; i++) {
        result += String.fromCharCode(
          text.charCodeAt(i) ^ key.charCodeAt(i % key.length)
        );
      }
      return result;
    } catch (error) {
      logger.error('Decryption failed', {
        component: 'DataProtection',
        action: 'decrypt'
      }, error);
      return encryptedText; // Fallback to encrypted text
    }
  }
  
  // Hash generation for sensitive data
  static async generateHash(data: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);
      const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      logger.error('Hash generation failed', {
        component: 'DataProtection',
        action: 'generateHash'
      }, error);
      return data; // Fallback
    }
  }
}

// Security headers management
export class SecurityHeaders {
  static getSecurityHeaders(): Record<string, string> {
    return {
      'X-Content-Type-Options': 'nosniff',
      'X-Frame-Options': 'DENY',
      'X-XSS-Protection': '1; mode=block',
      'Referrer-Policy': 'strict-origin-when-cross-origin',
      'Permissions-Policy': 'geolocation=(), microphone=(), camera=()',
      'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload'
    };
  }
  
  static applySecurityHeaders(): void {
    if (typeof document !== 'undefined') {
      // Apply meta tags for security
      const securityMetas = [
        { name: 'referrer', content: 'strict-origin-when-cross-origin' },
        { 'http-equiv': 'X-Content-Type-Options', content: 'nosniff' },
        { 'http-equiv': 'X-Frame-Options', content: 'DENY' },
        { 'http-equiv': 'X-XSS-Protection', content: '1; mode=block' }
      ];
      
      securityMetas.forEach(meta => {
        const existing = document.querySelector(`meta[${Object.keys(meta)[0]}="${Object.values(meta)[0]}"]`);
        if (!existing) {
          const metaTag = document.createElement('meta');
          Object.entries(meta).forEach(([key, value]) => {
            metaTag.setAttribute(key, value);
          });
          document.head.appendChild(metaTag);
        }
      });
    }
  }
}

// Initialize security measures
export const initializeSecurity = () => {
  SecurityHeaders.applySecurityHeaders();
  
  logger.info('Security measures initialized', {
    component: 'Security',
    action: 'initialize'
  }, {
    features: [
      'Input sanitization',
      'Rate limiting',
      'Content Security Policy',
      'Security headers',
      'Data protection utilities'
    ]
  });
};